/**
 * User Schema Types
 * 
 * This file defines the core user data structure that accurately reflects
 * the Supabase database tables and their relationships.
 * 
 * The schema is designed to eliminate confusion between different user roles
 * and provide a consistent interface for accessing user data throughout the application.
 */

import type { BusinessRole, UserRole } from './roles';

/**
 * Supabase Auth User (auth.users table)
 * This represents the core user authentication data from Supabase Auth
 */
export interface SupabaseAuthUser {
  id: string;
  email: string;
  created_at: string;
  updated_at: string;
  user_metadata?: Record<string, any>;
  app_metadata?: {
    provider?: string;
    business_access?: boolean;
    charity_access?: boolean;
    super_admin_access?: boolean;
    [key: string]: any;
  };
}

/**
 * Public User (public.users table)
 * This represents the user data stored in the public.users table
 */
export interface PublicUser {
  user_id: string; // References auth.users.id
  email: string;
  status: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * User Profile (profiles table)
 * This represents the user profile data stored in the profiles table
 */
export interface UserProfile {
  id: string; // References auth.users.id
  user_id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  avatar_url?: string;
  business_id?: string;
  role?: UserRole;
  created_at?: string;
  updated_at?: string;
}

/**
 * Business Staff (business_staff table)
 * This represents the business staff data stored in the business_staff table
 */
export interface BusinessStaffRecord {
  staff_id: string;
  user_id: string; // References auth.users.id
  business_id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone_number?: string;
  role: BusinessRole;
  status: 'active' | 'inactive' | 'invited' | 'pending';
  created_at: string;
  updated_at: string;
  last_login_at?: string;
  assigned_at: string;
}

/**
 * Normalized User
 * This is the consolidated user object that combines data from all relevant tables
 * and provides a consistent interface for accessing user data throughout the application.
 */
export interface NormalizedUser {
  // Core user identity (from auth.users)
  id: string;
  email: string;
  created_at: string;
  updated_at: string;
  
  // Application role (from profiles.role or user_metadata.role)
  role: UserRole;
  
  // Business context (if applicable)
  business_id?: string;
  businessStaffRole?: BusinessRole;
  
  // Charity context (if applicable)
  charity_id?: string;
  
  // Access flags (from app_metadata)
  business_access?: boolean;
  charity_access?: boolean;
  super_admin_access?: boolean;
  
  // Profile data
  profile?: {
    first_name?: string;
    last_name?: string;
    full_name?: string;
    phone?: string;
    avatar_url?: string;
  };
  
  // Common access properties (for backward compatibility)
  name?: string;                   // Full name for display purposes
  profilePicture?: string;         // Profile picture URL
  staff?: BusinessStaffRecord;     // Staff record for business users
  
  // Raw metadata (for backward compatibility)
  user_metadata?: Record<string, any>;
  app_metadata?: Record<string, any>;
  
  // References to related records
  publicUser?: PublicUser;
  userProfile?: UserProfile;
  businessStaff?: BusinessStaffRecord;
}

/**
 * Role Check Helper Functions
 * These functions provide a consistent way to check user roles
 * across the application, handling both production and dev mode.
 */

/**
 * Check if a user has business role/access
 */
export function isBusinessUser(user?: NormalizedUser | null): boolean {
  if (!user) return false;
  
  return (
    user.role === 'business' ||
    user.business_access === true ||
    (user.app_metadata?.business_access === true)
  );
}

/**
 * Check if a user has charity role/access
 */
export function isCharityUser(user?: NormalizedUser | null): boolean {
  if (!user) return false;
  
  return (
    user.role === 'charity' ||
    user.charity_access === true ||
    (user.app_metadata?.charity_access === true)
  );
}

/**
 * Check if a user is a super admin
 */
export function isSuperAdmin(user?: NormalizedUser | null): boolean {
  if (!user) return false;
  
  return (
    user.role === 'super_admin' ||
    user.super_admin_access === true ||
    (user.app_metadata?.super_admin_access === true) ||
    (user.app_metadata?.provider === 'dev_login' && user.app_metadata?.super_admin_access === true)
  );
}
